import { useState, useEffect, useRef } from 'react';

export default function useMenuVisible<T extends HTMLElement>(isVisible: boolean) {
    const [isMenuVisible, setIsMenuVisible] = useState(isVisible);
    const ref = useRef<T>(null);

    useEffect(() => {
        // each li is one menu item with one interactive child element (but possibly multiple child elements)
        const focusableElements = ref.current?.querySelector("ul")?.querySelectorAll("li") ?? [];
        const headerElement = focusableElements[0]?.children[0] as HTMLElement;
        const firstElement = focusableElements[1]?.querySelector("a, button") as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1]?.querySelector("a, button") as HTMLElement;

        const handleClick = (event: MouseEvent) => {
            ref.current!.blur();
    
            const target = event.target as T;
            if (!ref.current!.contains(target)) {
                setIsMenuVisible(false);
            } else {
                setIsMenuVisible(currentValue => !currentValue);
            }
        };
    
        const handleKeydown = (event: KeyboardEvent) => {
            const target = event.target as T;
            switch (event.key) {
                case "Escape":
                    setIsMenuVisible(false);
                    if (ref.current!.contains(target)) {
                        // must refocus on menu icon when closed
                        ref.current!.focus();
                    }
                    break;
                case "Enter":
                case " ":
                    if (ref.current!.contains(target)
                        && !isMenuVisible) {
                        event.preventDefault();
                        setIsMenuVisible(true);
                        // must focus on header when opened;
                        // focus doesn't work if menu isn't visible yet,
                        // so it needs a brief delay to work
                        setTimeout(() => headerElement.focus(), 1);
                    }
                    break;
                case "Tab":
                    if (isMenuVisible) {
                        if (event.shiftKey
                            && (document.activeElement === firstElement
                                || document.activeElement === headerElement)) {
                            event.preventDefault();
                            lastElement.focus();
                          } else if (!event.shiftKey
                            && document.activeElement === lastElement
                          ) {
                            event.preventDefault();
                            firstElement.focus();
                          }
                    }
                    break;
            } 
        };

        document.addEventListener('click', handleClick, true);
        document.addEventListener('keydown', handleKeydown, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
            document.removeEventListener('keydown', handleKeydown, true);
        };
    }, [isMenuVisible]);

    return { ref, isMenuVisible, setIsMenuVisible };
}